import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import SearchResults from "_shared/_api/search/search-results";
import { assertIsAxiosError } from "_shared/_errors/errors";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import MarketplaceCompanyAPIRequest from "marketplace/companies/_api/request-contracts/marketplace-company-api-request";
import MarketplaceCompanySearch from "marketplace/companies/_api/request-contracts/marketplace-company-search";
import UpdateMarketplaceCompanyAPIRequest from "marketplace/companies/_api/request-contracts/update-marketplace-company-api-request";
import Company from "marketplace/companies/company";
import CompanySchema, { CompanyJSON } from "marketplace/companies/company-schema";
import MarketplaceCompanyContactAPIRequest from "marketplace/companies/profiles/_api/request-contracts/marketplace-company-contact-api-request";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import Forum from "messaging/forums/forum";
import ForumSchema, { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import Session from "users/__session/session";

export default class CompanyAPIService {
  public constructor(params: { session: Readonly<Session>; abortController?: AbortController }) {
    this.abortController = params.abortController;
    if (!params.session.authToken) throw new Error("Cannot create CompanyAPIService without session.");
    this.headers.set("Authorization", `Bearer ${params.session.authToken.value}`);
  }

  public async getCompanyVendors(): Promise<CompanyJSON[]> {
    const url = new URL(`/marketplace/company-vendors`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: this.abortController?.signal,
      });
      return CompanySchema.validateJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      throw new CompanyApiServiceError("getCompanyVendors", error);
    }
  }

  public async searchCompanies(
    search: MarketplaceCompanySearch,
  ): Promise<SearchResults<MarketplaceSearchInfoJSON>> {
    const url = new URL(`/marketplace/companies?${search.searchParams.toString()}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: this.abortController?.signal,
      });
      return new SearchResults<MarketplaceSearchInfoJSON>(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      throw new CompanyApiServiceError("searchCompanies", error);
    }
  }

  public async getCompanyById(companyId: Guid): Promise<CompanyJSON | undefined> {
    const url = new URL(`/marketplace/companies/${companyId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: this.abortController?.signal,
      });
      return CompanySchema.validateJSON(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      if (!axios.isAxiosError(error)) {
        throw new CompanyApiServiceError("getCompanyById", error);
      }
      switch (error?.response?.status) {
        case HttpStatusCode.NotFound:
          throw new CompanyNotFoundError("Requested Company was not found");
        case HttpStatusCode.BadRequest:
          throw new HiddenCompanyError("Requested company is not visible");
        default:
          throw new CompanyApiServiceError("getCompanyById", error);
      }
    }
  }

  public async getCompaniesByIds(ids: Guid[], abortController?: AbortController): Promise<CompanyJSON[]> {
    const url = new URL("/marketplace/companies-by-ids", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        ids.map((id) => id.value),
        {
          headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
          signal: abortController?.signal,
        },
      );
      return CompanySchema.validateJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      throw new CompanyApiServiceError("getCompaniesByIds", error);
    }
  }

  public async getCompanyAvatar(companyId: Guid): Promise<string> {
    const url = new URL(`/marketplace/companies/${companyId.value}/avatar`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: this.abortController?.signal,
      });
      return response.data;
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      if (!axios.isAxiosError(error)) {
        throw new CompanyApiServiceError("getCompanyAvatar", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new CompanyAvatarNotFoundError(error.response.data);
        case HttpStatusCode.Forbidden:
          throw new HiddenCompanyError("Requested company is not visible");
        default:
          throw new CompanyApiServiceError("getCompanyAvatar", error);
      }
    }
  }

  public async getCompanyContactForums(companyId: Guid): Promise<ForumJSON[]> {
    try {
      const url = new URL(`/marketplace/companies/${companyId.value}/contact`, AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: this.abortController?.signal,
      });

      if (!response.data) return [];

      return ForumSchema.validateJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      if (!axios.isAxiosError(error)) {
        throw new CompanyApiServiceError("getCompanyContactForums", error);
      }
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new CompanyNotFoundError("Requested Company was not found");
      throw new CompanyApiServiceError("getCompanyContactForums", error);
    }
  }

  public async createCompany(company: Company): Promise<CompanyJSON> {
    const url = new URL(`/marketplace/companies`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(url.toString(), new MarketplaceCompanyAPIRequest(company), {
        headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
      });
      return CompanySchema.validateJSON(response.data);
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw new CompanyApiServiceError("createCompany", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new ManagerNotFoundError("Proposed manager for new company was not found");
        case HttpStatusCode.Unauthorized:
          throw new InvalidManagerError("Proposed manager for new company is not valid");
        default:
          throw new CompanyApiServiceError("createCompany", error);
      }
    }
  }

  public async updateCompany(
    companyId: Guid,
    isVisible: boolean,
    contactId?: Guid,
    originalProfile?: CompanyProfile,
    updatedProfile?: CompanyProfile,
  ): Promise<CompanyJSON> {
    const url = new URL(`/marketplace/companies/${companyId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.patch(
        url.toString(),
        new UpdateMarketplaceCompanyAPIRequest(isVisible, contactId, originalProfile, updatedProfile).payload,
        {
          headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
        },
      );
      return CompanySchema.validateJSON(response.data);
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw new CompanyApiServiceError("updateCompany", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
          throw new UpdateCompanyError(`Unable to update the company: ${error.response?.data}`);
        case HttpStatusCode.Forbidden:
          throw new InvalidManagerError("Manager for updated company is not valid");
        case HttpStatusCode.NotFound:
          throw new ManagerNotFoundError("Manager for updated company was not found");
        default:
          throw new CompanyApiServiceError("updateCompany", error);
      }
    }
  }

  public async joinCompany(companyId: Guid): Promise<void> {
    const url = new URL(`/marketplace/join-company/${companyId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(url.toString(), {
        headers: this.headers,
      });
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw new CompanyApiServiceError("joinCompany", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
        case HttpStatusCode.Forbidden:
          throw new UpdateCompanyError(`Unable to join company: ${error.response?.data}`);
        default:
          throw new CompanyApiServiceError("joinCompany", error);
      }
    }
  }

  public async registerCompanyContact(companyId: Guid, contact: MarketplaceCompanyContactAPIRequest): Promise<void> {
    const url = new URL(`/marketplace/register-company-contact/${companyId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(url.toString(), contact, {
        headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
      });
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw new CompanyApiServiceError("registerCompanyContact", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
        case HttpStatusCode.Forbidden:
          throw new UpdateCompanyError(`Unable to register company contact: ${error.response?.data}`);
        default:
          throw new CompanyApiServiceError("registerCompanyContact", error);
      }
    }
  }

  public async contactCompany(companyId: Guid): Promise<Forum> {
    const url = new URL(`/marketplace/companies/${companyId.value}/contact`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers,
        },
      );
      return new Forum(ForumSchema.deserialize(response.data));
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw new CompanyApiServiceError("contactCompany", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
          throw new UpdateCompanyError(`Unable to contact company: ${error.response?.data}`);
        case HttpStatusCode.NotFound:
          throw new CompanyNotFoundError("Requested Company was not found");
        default:
          throw new CompanyApiServiceError("contactCompany", error);
      }
    }
  }

  public async createCompanyAvatar(companyId: Guid, file: File): Promise<void> {
    const url = new URL(`/marketplace/companies/${companyId.value}/avatar`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(url.toString(), formData, {
        headers: this.headers.concat({ "Content-Type": "multipart/form-data" }),
      });
      AttorneyHubAPIService.removeHeader("Content-Type");
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw new CompanyApiServiceError("createCompanyAvatar", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new CompanyNotFoundError(error.response?.data);
        case HttpStatusCode.Forbidden:
          throw new InvalidManagerError("Manager for updated company is not valid");
        case HttpStatusCode.UnprocessableEntity:
          throw new CompanyAvatarError(error.response?.data);
        default:
          throw new CompanyApiServiceError("createCompanyAvatar", error);
      }
    }
  }

  public async deleteCompanyAvatar(companyId: Guid): Promise<void> {
    const url = new URL(`/marketplace/companies/${companyId.value}/avatar`, AttorneyHubAPIService.apiBaseUrl);

    try {
      await axios.delete(url.toString(), {
        headers: this.headers,
      });
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw new CompanyApiServiceError("deleteCompanyAvatar", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.Forbidden:
          throw new InvalidManagerError("Manager for updated company is not valid");
        case HttpStatusCode.NotFound:
          throw new CompanyNotFoundError("Requested Company was not found");
        default:
          throw new CompanyApiServiceError("deleteCompanyAvatar", error);
      }
    }
  }

  private readonly headers: AxiosHeaders = new AxiosHeaders();
  private readonly abortController?: AbortController;
}

export class CompanyApiServiceError extends Error {
  method: string;
  error: unknown;
  constructor(method: string, error: unknown) {
    super(`Error in CompanyAPIService.${method}: ${error instanceof Error ? error.message : String(error)}`);
    this.method = method;
    this.error = error;
  }
}
export class CompanyNotFoundError extends Error {}
export class HiddenCompanyError extends Error {}
export class CompanyAvatarError extends Error {}
export class ManagerNotFoundError extends Error {}
export class InvalidManagerError extends Error {}
export class UpdateCompanyError extends Error {}
export class CompanyAvatarNotFoundError extends Error {}
